/**
 * Table fermée des icônes de navigation nommées (UPT-2079 F2, web §5.3) : le vocabulaire du Module Spec v1.3
 * ({@code icon} d'un menu ou d'un élément de menu, `icon_key` d'un nœud de navigation). Import par nom, jamais de
 * `import *` (poids du lot) ; une clé hors de cette table n'est jamais rendue, elle retombe sur l'icône par défaut.
 * Le catalogue des capacités (`icons.heroicons`) est tiré de cette table par `pnpm catalog:build` : ajouter une icône ici,
 * puis relancer `pnpm catalog:build`.
 */
import type React from 'react';
import {
  AcademicCapIcon,
  ArrowPathRoundedSquareIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BeakerIcon,
  BellIcon,
  BoltIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CakeIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  CalendarIcon,
  CameraIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  CreditCardIcon,
  CubeIcon,
  CurrencyDollarIcon,
  CurrencyEuroIcon,
  DevicePhoneMobileIcon,
  DocumentIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  FaceSmileIcon,
  FireIcon,
  FlagIcon,
  FolderIcon,
  GiftIcon,
  GlobeAltIcon,
  HandRaisedIcon,
  HeartIcon,
  HomeIcon,
  IdentificationIcon,
  InboxIcon,
  InformationCircleIcon,
  KeyIcon,
  LifebuoyIcon,
  LightBulbIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MinusIcon,
  MoonIcon,
  NewspaperIcon,
  PencilSquareIcon,
  PhoneIcon,
  PhotoIcon,
  PlusIcon,
  PresentationChartLineIcon,
  PuzzlePieceIcon,
  QuestionMarkCircleIcon,
  ReceiptPercentIcon,
  RectangleStackIcon,
  ScaleIcon,
  ServerIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ShoppingCartIcon,
  SparklesIcon,
  Squares2X2Icon,
  StarIcon,
  SunIcon,
  TableCellsIcon,
  TagIcon,
  TicketIcon,
  TrashIcon,
  TrophyIcon,
  TruckIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  WalletIcon,
  WrenchIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export type NavIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export const NAV_ICONS: Readonly<Record<string, NavIconComponent>> = Object.freeze({
  AcademicCapIcon,
  ArrowPathRoundedSquareIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BeakerIcon,
  BellIcon,
  BoltIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CakeIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  CalendarIcon,
  CameraIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  CreditCardIcon,
  CubeIcon,
  CurrencyDollarIcon,
  CurrencyEuroIcon,
  DevicePhoneMobileIcon,
  DocumentIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  FaceSmileIcon,
  FireIcon,
  FlagIcon,
  FolderIcon,
  GiftIcon,
  GlobeAltIcon,
  HandRaisedIcon,
  HeartIcon,
  HomeIcon,
  IdentificationIcon,
  InboxIcon,
  InformationCircleIcon,
  KeyIcon,
  LifebuoyIcon,
  LightBulbIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MinusIcon,
  MoonIcon,
  NewspaperIcon,
  PencilSquareIcon,
  PhoneIcon,
  PhotoIcon,
  PlusIcon,
  PresentationChartLineIcon,
  PuzzlePieceIcon,
  QuestionMarkCircleIcon,
  ReceiptPercentIcon,
  RectangleStackIcon,
  ScaleIcon,
  ServerIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ShoppingCartIcon,
  SparklesIcon,
  Squares2X2Icon,
  StarIcon,
  SunIcon,
  TableCellsIcon,
  TagIcon,
  TicketIcon,
  TrashIcon,
  TrophyIcon,
  TruckIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  WalletIcon,
  WrenchIcon,
  XMarkIcon,
});

/** Icône rendue quand la clé est absente ou hors de la table. */
export const NAV_ICON_FALLBACK = Squares2X2Icon;

/** Composant de l'icône de navigation nommée `key`, ou `undefined` si elle est hors de la table fermée. */
export function navIconComponent(key: string | null | undefined): NavIconComponent | undefined {
  if (!key) return undefined;
  return Object.prototype.hasOwnProperty.call(NAV_ICONS, key) ? NAV_ICONS[key] : undefined;
}
